import React from 'react';
import { Check, Circle, CircleDot, AlertCircle, Loader2, CloudOff } from 'lucide-react';

// Report lifecycle status badge.
export const ReportStatusBadge = ({ status, className = '' }) => {
  const map = {
    draft: 'bg-slate-100 text-slate-700 border-slate-200',
    finalized: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    archived: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${map[status] || map.draft} ${className}`} data-testid={`report-status-${status}`}>
      {status}
    </span>
  );
};

// Save-state pill: 'saved' | 'saving' | 'unsaved' | 'error'.
export const SaveState = ({ state, className = '' }) => {
  const cfg = {
    saved: { icon: Check, text: 'Saved', cls: 'text-emerald-600' },
    saving: { icon: Loader2, text: 'Saving…', cls: 'text-slate-500', spin: true },
    unsaved: { icon: CircleDot, text: 'Unsaved changes', cls: 'text-amber-600' },
    error: { icon: CloudOff, text: 'Save failed', cls: 'text-rose-600' },
  }[state] || null;
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.cls} ${className}`} data-testid={`save-state-${state}`}>
      <Icon className={`h-3.5 w-3.5 ${cfg.spin ? 'animate-spin' : ''}`} />
      {cfg.text}
    </span>
  );
};

// Section state indicator for the builder nav.
// state: 'has_data' | 'no_data' | 'complete' | 'blocked' | 'authored'
export const SectionStateDot = ({ state }) => {
  const map = {
    has_data: { icon: CircleDot, cls: 'text-emerald-500', title: 'Has data' },
    complete: { icon: Check, cls: 'text-emerald-600', title: 'Complete' },
    authored: { icon: Check, cls: 'text-emerald-600', title: 'Written' },
    no_data: { icon: Circle, cls: 'text-slate-300', title: 'No data' },
    blocked: { icon: AlertCircle, cls: 'text-slate-400', title: 'No permission' },
  }[state] || { icon: Circle, cls: 'text-slate-300', title: '' };
  const Icon = map.icon;
  return <Icon className={`h-3.5 w-3.5 shrink-0 ${map.cls}`} title={map.title} />;
};

export const EmptyState = ({ message }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
    {message}
  </div>
);

// WS4 — latest historical assessment shown when none exists in-period.
export const OutsidePeriodBanner = ({ date, kind }) => (
  <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800" data-testid="outside-period-banner">
    <span className="mt-0.5 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium">Outside Review Period</span>
    <span>No new {kind} was recorded during this review period. The athlete's latest available assessment{date ? ` (${date})` : ''} is shown for reference.</span>
  </div>
);

export const BlockedState = ({ message }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center" data-testid="section-blocked">
    <AlertCircle className="mx-auto mb-1.5 h-5 w-5 text-slate-400" />
    <div className="text-sm text-slate-500">{message || "You don't have permission to include this section."}</div>
  </div>
);

// Stat card — null-safe (— for not recorded), no gaming meters.
export const StatCard = ({ label, value, unit, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-800">
      {value === null || value === undefined ? <span className="text-slate-300">—</span> : value}
      {unit && value !== null && value !== undefined ? <span className="text-sm font-normal text-slate-400 ml-1">{unit}</span> : null}
    </div>
  </div>
);

// Assessment pill (positive / needs_improvement / neutral) — professional,
// not aggressive traffic-light styling.
export const AssessmentPill = ({ assessment }) => {
  const map = {
    positive: { label: 'Positive', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    needs_improvement: { label: 'Needs Improvement', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
    neutral: { label: 'Neutral', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  }[assessment] || { label: assessment || 'Note', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${map.cls}`}>
      {map.label}
    </span>
  );
};

export const SectionCard = ({ title, subtitle, children, right = null, testId }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={testId}>
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);
